import { Component, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AuthFooter } from '../../../shared/components/auth-footer/auth-footer';
import { AuthService } from '../../../core/services/auth.service';
import { toast } from 'ngx-sonner';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    AppButton,
    TranslateModule,
    RouterModule,
    AuthFooter
  ],
  templateUrl: './forgot-password.html',
})
export class ForgotPassword {
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  isLoading = false;
  state: 0 | 1 = 0; // 0: Input Email, 1: Success Message

  forgotForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  async onSubmit() {
    if (this.isLoading) return;

    if (this.forgotForm.valid) {
      this.isLoading = true;
      this.cdr.detectChanges();

      const { email } = this.forgotForm.value;
      try {
        // Race against a 10s timeout to prevent infinite loading if the
        // redirectTo URL isn't whitelisted in Supabase Dashboard → Authentication → URL Configuration
        const timeout = new Promise<{ data: null; error: Error }>(resolve =>
          setTimeout(() => resolve({
            data: null,
            error: new Error('Request timed out. Check that your redirect URL is whitelisted in Supabase.')
          }), 10000)
        );

        const result = await Promise.race([
          this.authService.resetPassword(email),
          timeout
        ]) as { data: any; error: any };

        console.log('[ForgotPassword] resetPassword result:', result);

        if (result.error) {
          toast.error(this.translate.instant('error'), {
            description: result.error.message
          });
        } else {
          this.state = 1;
          toast.success(this.translate.instant('forgot_link_sent_title'), {
            description: this.translate.instant('forgot_link_sent_body', { email })
          });
        }
      } catch (err: any) {
        console.error('[ForgotPassword] unexpected error:', err);
        toast.error(this.translate.instant('error'), {
          description: err.message || this.translate.instant('forgot_send_failed')
        });
      } finally {
        // Supabase resolves outside Angular's NgZone, so we must manually
        // trigger change detection or isLoading=false / state=1 won't update in the view.
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    } else {
      this.forgotForm.markAllAsTouched();
    }
  }

  goBack() {
    this.router.navigate(['/login']);
  }
}
